import { CadCommandType } from '../../../command/CadCommandType';
import { resolveExtrudeFeatureKind } from '../../definition/extrude/ExtrudeFeatureKindResolver';
import { createFeatureReferenceBinding } from '../../../utils/feature/createFeatureReferenceBinding';
import {
    FeatureCommandAdapter,
    type FeatureCommandBuildContext,
    type FeatureCommandDependencyContext,
    type FeatureCommandDependencyInfo,
} from '../FeatureCommandAdapter';
import {
    isRecord,
    readCommandStringArrayParam,
    uniqueNonEmptyStrings,
} from '../../../utils/feature/featureCommandDependencyUtils';
import { resolveSketchProfileSourceSketchId } from '../../../utils/feature/resolveSketchProfileSourceSketchId';
import { ExtrudeFeatureDefinitionModel } from '../../definition/extrude/ExtrudeFeatureDefinition';
import { BossExtrudeFeaturePrototype } from '../../prototype/extrude/BossExtrudeFeaturePrototype';
import { CutExtrudeFeaturePrototype } from '../../prototype/extrude/CutExtrudeFeaturePrototype';
import type { ExtrudeFeaturePrototype } from '../../prototype/extrude/ExtrudeFeaturePrototype';

/**
 * 从普通对象字段读取非空字符串。
 *
 * @param record 普通对象。
 * @param key 字段名。
 * @returns 非空字符串；字段无效时返回 null。
 */
function readStringField(record: Record<string, unknown>, key: string): string | null {
    const value = record[key];
    return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * 从普通对象字段读取非空字符串数组。
 *
 * @param record 普通对象。
 * @param key 字段名。
 * @returns 过滤空值后的字符串数组。
 */
function readStringArrayField(record: Record<string, unknown>, key: string): string[] {
    const value = record[key];
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

/**
 * 收集拉伸切除定义中持久化的目标体依赖。
 *
 * @param command 拉伸命令。
 * @returns 目标体 ID 列表。
 */
function collectCutExtrudeTargetDependencyIds(command: CadCommand): string[] {
    const params = isRecord(command.params) ? command.params : {};
    const cutOptions = isRecord(params.cutOptions) ? params.cutOptions : {};
    const hostedContourInput = isRecord(cutOptions.hostedContourInput) ? cutOptions.hostedContourInput : {};
    const featureScope = isRecord(cutOptions.featureScope) ? cutOptions.featureScope : {};

    return uniqueNonEmptyStrings([
        readStringField(hostedContourInput, 'targetBodyId'),
        ...readStringArrayField(featureScope, 'initialAutoTargetIds'),
        ...readStringArrayField(featureScope, 'manualTargetIds'),
    ]);
}

/**
 * 拉伸命令适配器。
 */
export class ExtrudeCommandAdapter extends FeatureCommandAdapter<ExtrudeFeaturePrototype> {
    /**
     * @inheritdoc
     */
    public supports(command: CadCommand): boolean {
        if (command.type !== CadCommandType.EXTRUDE) {
            return false;
        }
        try {
            resolveExtrudeFeatureKind(command.params as ExtrudeParams);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * @inheritdoc
     */
    public createPrototypeFromCommand(command: CadCommand): ExtrudeFeaturePrototype | null {
        if (!this.supports(command)) {
            return null;
        }

        const params = structuredClone(command.params as ExtrudeParams);
        const featureKind = resolveExtrudeFeatureKind(params);
        const definition = ExtrudeFeatureDefinitionModel.fromParams({
            featureKind,
            commandParams: params,
        });
        const bindings = (params.referenceBindings ?? []).map((binding) => createFeatureReferenceBinding(binding));
        const sourceSketchId = resolveSketchProfileSourceSketchId({
            definitionSourceSketchId: definition.getSourceSketchId(),
            bindings,
        });

        if (featureKind === 'cut-extrude') {
            return new CutExtrudeFeaturePrototype({
                featureId: command.id,
                definition,
                bindings,
                sourceSketchId,
            });
        }

        return new BossExtrudeFeaturePrototype({
            featureId: command.id,
            definition,
            bindings,
            sourceSketchId,
        });
    }

    /**
     * @inheritdoc
     */
    public collectDependencyInfo(
        context: FeatureCommandDependencyContext<ExtrudeFeaturePrototype>,
    ): FeatureCommandDependencyInfo {
        const dependencyInfo = super.collectDependencyInfo(context);
        const targetSolidIds = readCommandStringArrayParam(context.command, 'targetSolidIds');
        const cutTargetDependencyIds = collectCutExtrudeTargetDependencyIds(context.command);
        const params = context.command.params as ExtrudeParams;
        const hasThroughAll = params.direction1?.endCondition === 'through_all'
            || (params.direction1?.endCondition !== 'mid_plane' && params.direction2?.enabled && params.direction2.endCondition === 'through_all');

        return {
            ...dependencyInfo,
            // 范围依赖从正式前缀派生；Worker 不得把求值结果写回参数而破坏 replay 哈希。
            rebuildReferencedModelIds: hasThroughAll ? (context.commands ?? [])
                .filter((command, index) => index < (context.commandIndex ?? 0)
                    && (!params.documentScopeNodeKey || command.params.documentScopeNodeKey === params.documentScopeNodeKey))
                .map((command) => command.id) : [],
            referencedModelIds: uniqueNonEmptyStrings([
                ...dependencyInfo.referencedModelIds,
                ...targetSolidIds,
                ...cutTargetDependencyIds,
            ]),
        };
    }

    /**
     * @inheritdoc
     */
    public createCommandFromPrototype(context: FeatureCommandBuildContext<ExtrudeFeaturePrototype>): CadCommand {
        const commandParams = context.prototype.getDefinition().getCommandParams();
        const serializedBindings = context.prototype.getBindings().map((binding) => binding.serialize());
        return {
            id: context.prototype.getFeatureId(),
            type: CadCommandType.EXTRUDE,
            clientId: context.clientId,
            timestamp: context.timestamp ?? Date.now(),
            params: {
                ...commandParams,
                featureKind: context.prototype.getFeatureKind(),
                referenceBindings: serializedBindings,
            },
        };
    }
}
